import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';

interface TypingUser {
  userId: string;
  displayName: string;
  avatarUrl: string | null;
}

export function useTypingIndicator(channelName: string) {
  const { user } = useAuth();
  const [typingUsers, setTypingUsers] = useState<TypingUser[]>([]);
  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isTypingRef = useRef(false);

  // Fetch current user's profile for broadcasting
  const [userProfile, setUserProfile] = useState<{ displayName: string; avatarUrl: string | null } | null>(null);

  useEffect(() => {
    if (!user) return;

    const fetchProfile = async () => {
      const { data } = await supabase
        .from('profiles')
        .select('display_name, avatar_url')
        .eq('user_id', user.id)
        .maybeSingle();

      if (data) {
        setUserProfile({
          displayName: data.display_name || 'Anonymous',
          avatarUrl: data.avatar_url,
        });
      }
    };

    fetchProfile();
  }, [user]);

  useEffect(() => {
    if (!channelName) return;

    const channel = supabase.channel(`typing:${channelName}`, {
      config: {
        presence: {
          key: user?.id || 'anonymous',
        },
      },
    });

    channel
      .on('broadcast', { event: 'typing' }, ({ payload }) => {
        if (payload.userId === user?.id) return;

        setTypingUsers((prev) => {
          const exists = prev.some((u) => u.userId === payload.userId);
          if (payload.isTyping) {
            if (exists) return prev;
            return [...prev, {
              userId: payload.userId,
              displayName: payload.displayName,
              avatarUrl: payload.avatarUrl,
            }];
          } else {
            return prev.filter((u) => u.userId !== payload.userId);
          }
        });

        // Auto-remove after 3 seconds of no updates
        if (payload.isTyping) {
          setTimeout(() => {
            setTypingUsers((prev) => prev.filter((u) => u.userId !== payload.userId));
          }, 3000);
        }
      })
      .subscribe();

    channelRef.current = channel;

    return () => {
      supabase.removeChannel(channel);
      channelRef.current = null;
    };
  }, [channelName, user?.id]);

  const startTyping = useCallback(() => {
    if (!user || !channelRef.current || !userProfile || isTypingRef.current) return;

    isTypingRef.current = true;
    channelRef.current.send({
      type: 'broadcast',
      event: 'typing',
      payload: {
        userId: user.id,
        displayName: userProfile.displayName,
        avatarUrl: userProfile.avatarUrl,
        isTyping: true,
      },
    });

    // Clear any existing timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    // Auto-stop typing after 2 seconds of inactivity
    typingTimeoutRef.current = setTimeout(() => {
      stopTyping();
    }, 2000);
  }, [user, userProfile]);

  const stopTyping = useCallback(() => {
    if (!user || !channelRef.current || !isTypingRef.current) return;

    isTypingRef.current = false;
    channelRef.current.send({
      type: 'broadcast',
      event: 'typing',
      payload: {
        userId: user.id,
        displayName: userProfile?.displayName || 'Anonymous',
        avatarUrl: userProfile?.avatarUrl || null,
        isTyping: false,
      },
    });

    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = null;
    }
  }, [user, userProfile]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    };
  }, []);

  return {
    typingUsers,
    startTyping,
    stopTyping,
  };
}
